import PropTypes from 'prop-types';
import React from 'react';
import Dropzone from 'react-dropzone';

const ACCEPTED_TYPES = 'application/pdf,image/jpeg,image/png,image/webp,image/gif,message/rfc822,.pdf,.jpg,.jpeg,.png,.webp,.gif,.eml';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (fileName, mimeType) => {
  if (mimeType?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName)) {
    return 'fal fa-file-image text-primary';
  }
  if (mimeType === 'message/rfc822' || /\.eml$/i.test(fileName)) {
    return 'fal fa-envelope text-info';
  }
  return 'fal fa-file-pdf text-danger';
};

const PdfDropzone = ({
  selectedFiles,
  onFilesSelected,
  onRemoveFile,
  onUpload,
  onCancel,
  isUploading,
  maxFiles,
  hideActions
}) => {
  const handleDrop = (acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size
    }));
    onFilesSelected(newFiles);
  };

  const dropzoneProps = {
    onDrop: handleDrop,
    accept: ACCEPTED_TYPES,
    multiple: true
  };
  if (maxFiles) {
    dropzoneProps.maxFiles = maxFiles;
  }

  if (selectedFiles.length === 0) {
    return (
      <Dropzone {...dropzoneProps}>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            className="border rounded p-5 text-center"
            style={{
              cursor: "pointer",
              borderStyle: "dashed",
              borderColor: isDragActive ? "var(--sl-fg-brand)" : "var(--sl-border-primary)",
              backgroundColor: isDragActive ? "var(--sl-bg-active)" : "var(--sl-bg-secondary)",
              minHeight: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              transition: "all 0.2s ease"
            }}
          >
            <input {...getInputProps()} />
            <i
              className="fal fa-file-upload"
              style={{
                fontSize: '60px',
                color: isDragActive ? 'var(--sl-fg-brand)' : 'var(--sl-text-tertiary)',
                marginBottom: '16px'
              }}
            />
            <h5 className="mb-2" style={{ color: 'var(--sl-text-secondary)' }}>
              {isDragActive ? 'Drop files here' : 'Drop documents here'}
            </h5>
            <p className="mb-0" style={{ color: 'var(--sl-text-tertiary)' }}>PDF, images, or email files</p>
          </div>
        )}
      </Dropzone>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <h6 className="mb-2">Selected Files ({selectedFiles.length})</h6>
        <div className="list-group">
          {selectedFiles.map((fileData) => (
            <div
              key={fileData.id}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <div className="d-flex align-items-center" style={{ flex: 1 }}>
                <i className={`${getFileIcon(fileData.name, fileData.file?.type)} fa-2x mr-3`} />
                <div>
                  <div className="font-weight-bold">{fileData.name}</div>
                  <small className="text-muted">{formatFileSize(fileData.size)}</small>
                </div>
              </div>
              <button
                className="btn btn-link text-danger p-0"
                onClick={() => onRemoveFile(fileData.id)}
                style={{ fontSize: '20px' }}
                disabled={isUploading}
              >
                <i className="fal fa-times" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <Dropzone
          {...dropzoneProps}
          disabled={isUploading}
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <button
                type="button"
                className="btn btn-link p-0"
                disabled={isUploading}
              >
                <i className="fal fa-plus mr-2" />
                Add More Files
              </button>
            </div>
          )}
        </Dropzone>
        {!hideActions && (
          <div>
            <button
              className="btn btn-secondary mr-2"
              onClick={onCancel}
              disabled={isUploading}
            >
              Clear
            </button>
            <button
              className="btn btn-primary"
              onClick={onUpload}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <i className="fal fa-spinner fa-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <i className="fal fa-upload mr-2" />
                  Upload {selectedFiles.length} {selectedFiles.length === 1 ? 'File' : 'Files'}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

PdfDropzone.propTypes = {
  selectedFiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      file: PropTypes.object.isRequired,
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired
    })
  ).isRequired,
  onFilesSelected: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
  onCancel: PropTypes.func,
  isUploading: PropTypes.bool,
  maxFiles: PropTypes.number,
  hideActions: PropTypes.bool
};

PdfDropzone.defaultProps = {
  isUploading: false,
  maxFiles: undefined,
  hideActions: false
};

export default PdfDropzone;
